import cloneDeep from 'lodash/cloneDeep';
import { ReactNode, RefObject, useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { SanitizeHTML } from 'app/utils/htmlSanitizer';
import { sanitizationProfiles } from 'app/utils/htmlSanitizer/profiles';
import { copilotActionContexts } from 'components/copilotActions';
import { CopilotPinnedActions } from 'components/copilotPinnedActions';
import { EditorCopilotDropdown } from 'components/editorCopilotDropdown';
import type { SubjectField } from 'components/editorCopilotDropdown/components/translate';
import { useGetComposeModalCopilotActions } from 'components/editorCopilotDropdown/utils/composeModalCopilotActions';
import { EditorMentions } from 'components/editorMentions';
import { EditorQuickRepliesDropdown } from 'components/editorQuickRepliesDropdown';
import type { ChangeSubjectFn } from 'components/editorQuickRepliesDropdown/editorQuickRepliesDropdown.types';
import { HookFormRichTextField, RichTextFieldRef } from 'components/hookForm';

import type { FormValues } from '../composeEmailModal.types';
import type { AttachmentUpload } from '../hooks/useAttachments';

import styles from './composeEmailFieldRichText.module.scss';

type ComposeEmailFieldRichTextProps = {
  name: 'body' | 'internalNote';
  label: string;
  placeholder: string;
  required?: boolean;
  editorRef?: RefObject<RichTextFieldRef>;
  onChangeSubject?: ChangeSubjectFn;
  subjectField?: SubjectField;
  attachmentsFields?: ReactNode;
  onAttachmentAdded?: (attachment: AttachmentUpload) => void;
  attachmentCount?: number;
  onTriggerSend?: () => void;
  onFileIsUploadingChange?: (isUploading: boolean) => void;
  enduserId?: string | null;
  csid?: number | null;
};

export const ComposeEmailFieldRichText = ({
  name,
  label,
  placeholder,
  required = false,
  editorRef,
  onChangeSubject,
  subjectField,
  attachmentsFields,
  onAttachmentAdded,
  attachmentCount = 0,
  onTriggerSend,
  onFileIsUploadingChange,
  enduserId = null,
  csid = null,
}: ComposeEmailFieldRichTextProps): JSX.Element => {
  const { control } = useFormContext<FormValues>();
  const text = useWatch({ name, control }) || '';

  const { editorCopilotActions, internalNoteEditorCopilotActions } =
    useGetComposeModalCopilotActions({
      channel: 'email',
      context: copilotActionContexts.composeEmail,
      noteContext: copilotActionContexts.composeEmailNote,
      subjectField,
    });

  const isNote = name === 'internalNote';
  const copilotActions = isNote
    ? internalNoteEditorCopilotActions
    : editorCopilotActions;
  const editorClassName = isNote
    ? styles.internalNoteEditor
    : styles.messageEditor;

  // Passed to InputRichText so its attachment button shows the badge indicator
  // when files have been added. The array content is unused — only length matters.
  const attachmentFiles = useMemo<File[]>(
    () => Array.from({ length: attachmentCount }, () => new File([], '')),
    [attachmentCount],
  );

  const sanitize = useCallback((html: string) => {
    const profile = cloneDeep(sanitizationProfiles['email']);
    profile.allowedAttributes = {
      ...profile.allowedAttributes,
      span: ['contenteditable', 'data-variable-name', 'data-variable-label'],
    };

    return SanitizeHTML.customSanitize(html, profile, true);
  }, []);

  return (
    <HookFormRichTextField
      name={name}
      label={label}
      control={control}
      contentType="html"
      editorRef={editorRef}
      rules={
        required
          ? {
              required: 'Input is required',
            }
          : undefined
      }
      componentProps={{
        editorClassName: styles.editorWrapper,
        wrapperClassName: styles.editorWrapper,
        className: editorClassName,
        placeholder,
        showCharacterLimit: false,
        channel: isNote ? 'notes' : 'email',
        sanitize,
        hasBorder: true,
        hideSendOnEnterToggle: true,
        disableSubmitMessageWithEnter: true,
        attachmentsFields,
        onAttachmentAdded,
        attachments: attachmentFiles,
        onTriggerSend,
        onFileIsUploadingChange,
        toolbarAddons: (
          <>
            {isNote && <EditorMentions />}
            <EditorQuickRepliesDropdown
              enduserId={enduserId}
              csid={csid}
              changeSubject={onChangeSubject}
            />
            <EditorCopilotDropdown
              editorCopilotActions={copilotActions}
              channel="email"
            />
            <CopilotPinnedActions
              context={
                isNote
                  ? copilotActionContexts.composeEmailNote
                  : copilotActionContexts.composeEmail
              }
              channel="email"
              text={text}
            />
          </>
        ),
      }}
    />
  );
};
